import { useEffect, useState } from "react";
import axios from "axios";
import {
  Alert,
  AlertTitle,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LinkIcon from "@mui/icons-material/Link";
import { API_BASE_URL } from "../config";
import { useFullScreenDialog } from "../useFullScreenDialog";

const ITEMS_API = `${API_BASE_URL}/inventory/items`;

// Per-instance columns are only readable for a handful of containers; beyond that the
// same information is reached by expanding a line.
const MATRIX_LIMIT = 4;

// What each resolution does to a line, in the user's terms.
const ACTIONS = [
  { value: "link", label: "Shared", hint: "Keep this line the same in every container" },
  { value: "propagate", label: "Add to all", hint: "Create it in the containers that don't have it" },
  { value: "extra", label: "Leave", hint: "Not part of the kit — leave it wherever it already is" },
];

const TREE_INDENT = 2.5;

export default function KitLinkDialog({
  open, source, containers, pathLabel, kitName, onClose, onDone,
}) {
  const fullScreen = useFullScreenDialog();
  const [picked, setPicked] = useState([]);
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState("");
  const [resolutions, setResolutions] = useState({});
  const [differencesOnly, setDifferencesOnly] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [result, setResult] = useState(null);

  const kitId = source?.kit_id ?? null;

  useEffect(() => {
    if (!open) return;
    setPicked([]);
    setPlan(null);
    setResult(null);
    setResolutions({});
    setExpanded({});
    setDifferencesOnly(true);
    setName("");
  }, [open]);

  // Containers that could join: anything that can hold items, minus this one, minus
  // anything already spoken for by another kit.
  const options = containers.filter(c =>
    c.id !== source?.id && (!c.kit_id || c.kit_id === kitId));

  const itemIds = [source?.id, ...picked.map(p => p.id)].filter(Boolean);

  const check = () => {
    setLoading(true);
    setResult(null);
    axios.post(`${ITEMS_API}/link-preview`, { item_ids: itemIds, kit_id: kitId })
      .then(res => {
        setPlan(res.data);
        setName(res.data.kit_name || "");
        // Start from the plan's own defaults; a line it can't decide is left alone
        // until the user says what it should be.
        setResolutions(Object.fromEntries(res.data.lines.map(l => [l.key, {
          action: l.action,
          required_quantity: l.required_suggested,
        }])));
      })
      .finally(() => setLoading(false));
  };

  const apply = () => {
    setSaving(true);
    const payload = {
      item_ids: itemIds,
      kit_id: kitId,
      kit_name: name.trim() || null,
      resolutions: plan.lines
        .filter(l => resolutions[l.key]?.action && resolutions[l.key].action !== "decide")
        .map(l => ({
          key: l.key,
          action: resolutions[l.key].action,
          required_quantity: resolutions[l.key].required_quantity ?? null,
        })),
    };
    axios.post(`${ITEMS_API}/link-apply`, payload)
      .then(res => { setResult(res.data); onDone(); })
      .finally(() => setSaving(false));
  };

  const setAction = (key, action) =>
    setResolutions(r => ({ ...r, [key]: { ...r[key], action } }));
  const setRequired = (key, value) =>
    setResolutions(r => ({ ...r, [key]: {
      ...r[key], required_quantity: value === "" ? null : Number(value) } }));

  const bulk = (action) =>
    setResolutions(r => Object.fromEntries(plan.lines.map(l => [l.key, {
      ...r[l.key],
      // Only lines that are actually missing somewhere are affected — a line already
      // in every container has nothing to add or leave out.
      action: l.missing.length ? action : (r[l.key]?.action || l.action),
    }])));

  const instances = plan?.instances ?? [];
  const matrix = instances.length > 0 && instances.length <= MATRIX_LIMIT;
  const interesting = (l) => l.missing.length > 0 || l.required_conflict
    || l.structure_conflict || l.cells.some(c => c.ambiguous);
  const shown = (plan?.lines ?? []).filter(l => !differencesOnly || interesting(l));
  const counts = (plan?.lines ?? []).reduce((acc, l) => {
    const a = resolutions[l.key]?.action || l.action;
    return { ...acc, [a]: (acc[a] || 0) + 1 };
  }, {});
  const undecided = (plan?.lines ?? []).filter(l =>
    (resolutions[l.key]?.action || l.action) === "decide").length;

  const cellFor = (line, instanceId) => line.cells.find(c => c.instance_id === instanceId);

  const lineRow = (line) => {
    const action = resolutions[line.key]?.action || line.action;
    const open = Boolean(expanded[line.key]);
    const problem = line.structure_conflict || line.cells.some(c => c.ambiguous);
    return [
      <TableRow key={line.key} hover>
        <TableCell>
          <Stack direction="row" spacing={1} useFlexGap
                 sx={{ alignItems: "center", ml: line.depth * TREE_INDENT }}>
            {!matrix && (
              <IconButton size="small" aria-label={open ? "Collapse" : "Expand"}
                          onClick={() => setExpanded(e => ({ ...e, [line.key]: !open }))}>
                {open ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
              </IconButton>
            )}
            <Box>
              <Typography variant="body2" sx={{ fontWeight: line.is_container ? 600 : 400 }}>
                {line.label}
              </Typography>
              {line.path && (
                <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                  in {line.path}
                </Typography>
              )}
              {problem && (
                <Typography variant="caption" sx={{ color: "warning.main", display: "block" }}>
                  {line.structure_conflict
                    ? "Sits somewhere different in different containers"
                    : "More than one of these in one container — say which it is, or leave it"}
                </Typography>
              )}
            </Box>
          </Stack>
        </TableCell>

        {matrix
          ? instances.map(inst => {
              const cell = cellFor(line, inst.id);
              return (
                <TableCell key={inst.id} align="center">
                  {cell ? (
                    <Tooltip title={cell.has_stock ? `Holding ${cell.quantity}` : "Empty"}>
                      <Box component="span">
                        <CheckIcon fontSize="small" color="success" />
                      </Box>
                    </Tooltip>
                  ) : (
                    <Typography component="span" color="text.disabled">—</Typography>
                  )}
                </TableCell>
              );
            })
          : (
            <TableCell align="right">
              <Typography variant="body2"
                          sx={line.missing.length ? { color: "warning.main", fontWeight: 600 } : undefined}>
                {line.cells.length} of {instances.length}
              </Typography>
            </TableCell>
          )}

        <TableCell align="right">
          <TextField type="number" size="small" sx={{ width: 92 }}
                     value={resolutions[line.key]?.required_quantity ?? ""}
                     error={line.required_conflict}
                     disabled={action === "extra"}
                     onChange={e => setRequired(line.key, e.target.value)} />
          {line.required_conflict && (
            <Typography variant="caption" sx={{ color: "warning.main", display: "block" }}>
              was {line.cells.map(c => c.required_quantity ?? "—").join(", ")}
            </Typography>
          )}
        </TableCell>

        <TableCell align="right">
          <ToggleButtonGroup size="small" exclusive value={action}
                             onChange={(_, v) => v && setAction(line.key, v)}>
            {ACTIONS.map(a => (
              <ToggleButton key={a.value} value={a.value}
                            disabled={a.value === "propagate" && line.missing.length === 0}>
                <Tooltip title={a.hint}><Box component="span">{a.label}</Box></Tooltip>
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </TableCell>
      </TableRow>,
      !matrix && open && (
        <TableRow key={`${line.key}-detail`}>
          <TableCell colSpan={4} sx={{ pt: 0, bgcolor: "action.hover" }}>
            <Stack spacing={0.5} sx={{ py: 1 }}>
              {instances.map(inst => {
                const cell = cellFor(line, inst.id);
                return (
                  <Stack key={inst.id} direction="row" spacing={1} useFlexGap
                         sx={{ alignItems: "center" }}>
                    <Typography variant="caption" sx={{ minWidth: 200 }}>{inst.label}</Typography>
                    {cell ? (
                      <Typography variant="caption" color="text.secondary">
                        required {cell.required_quantity ?? "—"} · holding {cell.quantity ?? 0}
                        {cell.ambiguous ? " · more than one match" : ""}
                      </Typography>
                    ) : (
                      <Typography variant="caption" sx={{ color: "warning.main" }}>not there</Typography>
                    )}
                  </Stack>
                );
              })}
            </Stack>
          </TableCell>
        </TableRow>
      ),
    ];
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg" fullScreen={fullScreen}>
      <DialogTitle>
        {kitId ? `Add containers to the ${kitName(kitId)} kit` : "Link identical containers"}
        <Typography variant="body2" color="text.secondary">
          {source ? pathLabel(source) : ""}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Autocomplete
            multiple
            options={options}
            getOptionLabel={pathLabel}
            getOptionKey={(c) => c.id}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            value={picked}
            onChange={(_, v) => { setPicked(v); setPlan(null); setResult(null); }}
            renderInput={(p) => (
              <TextField {...p} label="Containers that should hold the same things"
                         helperText={`${itemIds.length} selected, including ${
                           source ? pathLabel(source) : "this one"}`} />
            )}
          />

          {plan === null ? (
            <Alert severity="info">
              Pick the other containers, then check for differences. Nothing is changed
              until you've seen what it would do.
            </Alert>
          ) : (
            <>
              {plan.warnings.map((w, i) => <Alert key={i} severity="warning">{w}</Alert>)}
              {plan.suggestions.map((s, i) => (
                <Alert key={`s${i}`} severity="info">
                  <AlertTitle>{s.labels.join("  /  ")}</AlertTitle>
                  {s.reason}
                </Alert>
              ))}
              {plan.identical ? (
                <Alert severity="success">
                  No differences found — these containers already hold the same things in
                  the same required quantities. Linking them just keeps it that way.
                </Alert>
              ) : (
                <Alert severity="info">
                  {[
                    counts.link ? `${counts.link} already shared` : null,
                    counts.propagate ? `${counts.propagate} to be created where missing` : null,
                    counts.extra ? `${counts.extra} left outside the kit` : null,
                    undecided ? `${undecided} needing a decision` : null,
                  ].filter(Boolean).join(" · ")}
                  {". Nothing is deleted and no actual stock is touched."}
                </Alert>
              )}

              <Stack direction={{ xs: "column", sm: "row" }} spacing={2} useFlexGap
                     sx={{ alignItems: "center", flexWrap: "wrap" }}>
                <TextField label="Kit name" size="small" sx={{ minWidth: 220 }}
                           value={name} disabled={Boolean(kitId)}
                           onChange={e => setName(e.target.value)} />
                <FormControlLabel
                  control={<Switch checked={differencesOnly}
                                   onChange={e => setDifferencesOnly(e.target.checked)} />}
                  label="Differences only"
                />
                <Box sx={{ flex: 1 }} />
                <Button size="small" onClick={() => bulk("propagate")}>Add all missing</Button>
                <Button size="small" onClick={() => bulk("extra")}>Leave all missing</Button>
              </Stack>

              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ "& th": { fontWeight: 700 } }}>
                      <TableCell>Item</TableCell>
                      {matrix
                        ? instances.map(inst => (
                            <TableCell key={inst.id} align="center">
                              {inst.label}
                              {inst.existing && (
                                <Typography variant="caption" color="text.secondary"
                                            sx={{ display: "block" }}>
                                  already in the kit
                                </Typography>
                              )}
                            </TableCell>
                          ))
                        : <TableCell align="right">Present in</TableCell>}
                      <TableCell align="right">Required</TableCell>
                      <TableCell align="right">What to do</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {shown.map(lineRow)}
                    {shown.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={matrix ? instances.length + 3 : 4} align="center"
                                   sx={{ color: "text.secondary", py: 3 }}>
                          {plan.lines.length === 0
                            ? "These containers are empty."
                            : "Nothing differs — turn off “Differences only” to see every line."}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}

          {result && (
            <Alert severity="success">
              Linked. {result.linked} {result.linked === 1 ? "item" : "items"} now shared
              {result.created ? `, ${result.created} created where missing` : ""}
              {result.required_updated
                ? `, ${result.required_updated} required quantit${result.required_updated === 1 ? "y" : "ies"} brought into line`
                : ""}.
              {result.created > 0 && " The new items start empty, so they show as short " +
                "until they're packed."}
              {result.skipped.length > 0 && ` Skipped: ${result.skipped.join(" ")}`}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        {plan && !result && (
          <Chip size="small" icon={<LinkIcon />}
                label={`${instances.length} containers`} sx={{ mr: "auto" }} />
        )}
        <Button onClick={onClose} disabled={saving}>{result ? "Close" : "Cancel"}</Button>
        {!result && (
          <Button onClick={check} disabled={itemIds.length < 2 || loading}
                  startIcon={loading ? <CircularProgress size={16} /> : undefined}>
            {plan ? "Check again" : "Check for differences"}
          </Button>
        )}
        {plan && !result && (
          <Button variant="contained" startIcon={<LinkIcon />}
                  disabled={saving || !name.trim()} onClick={apply}>
            {saving ? "Linking" : "Link them"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
